// =============================================================================
// AppShell - casca global do CRM (sidebar colapsável + topbar + modal de config)
// =============================================================================
// Fase 1 do redesign "green" (ref.: C:\otmiz\knowledge\geral\layouts\cms\green).
//   • Sidebar colapsável (preferência em localStorage), dividida em seções, com
//     off-canvas no mobile.
//   • Itens de OPERAÇÃO ficam na sidebar; itens administrativos (Usuários,
//     Parâmetros Normativos, Instituições) + integrações (iSolve) ficam atrás da
//     engrenagem na topbar, num modal centralizado (só ADMIN enxerga a engrenagem).
//   • As páginas renderizam dentro do <main> via <Outlet/>; `currentUser` é
//     propagado por useOutletContext<ShellContext>().
// =============================================================================

import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Kanban, FilePlus2, AlertTriangle, ClipboardCheck,
  Settings, ChevronLeft, ChevronRight, Menu,
  Users, Building2, Link2, Mail, FolderOpen, Grid3x3, LogOut, X, Download, FileText, Puzzle, KeyRound, ListOrdered,
} from 'lucide-react';
import SinoNotificacoes from './SinoNotificacoes';
import type { CurrentUser } from '../hooks/useCurrentUser';
import { useSessaoISolve } from '../hooks/useSessaoISolve';
import { useDivergenciasPendentes } from '../hooks/useDivergenciasPendentes';
import { useAmbiente } from '../hooks/useAmbiente';
import type { AlertaSessaoISolve } from '../services/api';

export interface ShellContext {
  currentUser: CurrentUser | null;
}

interface AppShellProps {
  currentUser: CurrentUser | null;
}

const STORAGE_KEY = 'otmizcred_sidebar_colapsada';

function lerColapsadaInicial(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

// ── Navegação da sidebar, agrupada em seções ────────────────────────────────
interface NavDef {
  to: string;
  label: string;
  icon: typeof Kanban;
  end?: boolean;
  badge?: 'divergencias';
}

const SECOES_NAV: { titulo: string; itens: NavDef[] }[] = [
  {
    titulo: 'Operação',
    itens: [
      { to: '/', label: 'Quadro de Operações', icon: Kanban, end: true },
      { to: '/novas-operacoes', label: 'Entrada iSolve', icon: FilePlus2 },
      { to: '/documentos', label: 'Documentos', icon: FileText },
    ],
  },
  {
    titulo: 'Fila de trabalho',
    itens: [
      { to: '/divergencias', label: 'Divergências', icon: AlertTriangle, badge: 'divergencias' },
      { to: '/enquadramento', label: 'Enquadramento', icon: ClipboardCheck },
    ],
  },
];

// Semáforo iSolve (mesma semântica do antigo Header)
const ISOLVE_CFG: Record<AlertaSessaoISolve, { cor: string; anel: string; titulo: (d: number | null) => string }> = {
  ATIVA:      { cor: 'bg-green-500', anel: 'ring-green-200', titulo: () => 'Sessão iSolve ativa' },
  EXPIRANDO:  { cor: 'bg-amber-400', anel: 'ring-amber-200', titulo: (d) => `Expira em ${d ?? '?'} dias - reconecte quando puder` },
  SUSPEITA:   { cor: 'bg-amber-400', anel: 'ring-amber-200', titulo: () => 'Possível instabilidade - pode normalizar sozinha; se persistir, reconecte' },
  EXPIRADA:   { cor: 'bg-red-500',   anel: 'ring-red-200',   titulo: () => 'Sessão expirada - clique para reconectar' },
  SEM_SESSAO: { cor: 'bg-red-500',   anel: 'ring-red-200',   titulo: () => 'Sem sessão configurada - clique para conectar' },
};

export default function AppShell({ currentUser }: AppShellProps) {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const { sessao } = useSessaoISolve();
  const { pendentes: divergenciasPendentes } = useDivergenciasPendentes();
  const ambiente = useAmbiente();
  const isolveDesconectado = ambiente ? !ambiente.isolveConectado : false;

  const [colapsada, setColapsada] = useState<boolean>(lerColapsadaInicial);
  const [mobileAberto, setMobileAberto] = useState(false);
  const [configAberto, setConfigAberto] = useState(false);
  const [menuUsuario, setMenuUsuario] = useState(false);

  const isAdmin = currentUser?.papel === 'ADMIN';
  const conta = accounts[0];
  const nomeExibicao = currentUser?.nome ?? conta?.name ?? 'Usuário';
  const iniciais =
    nomeExibicao.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase() || 'U';
  const papelLabel = currentUser?.papel
    ? currentUser.papel.charAt(0) + currentUser.papel.slice(1).toLowerCase()
    : null;

  const alternarColapso = () => {
    setColapsada((prev) => {
      const novo = !prev;
      try { localStorage.setItem(STORAGE_KEY, novo ? '1' : '0'); } catch { /* ignore */ }
      return novo;
    });
  };

  const handleLogout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  const irPara = (to: string) => {
    setConfigAberto(false);
    setMenuUsuario(false);
    setMobileAberto(false);
    navigate(to);
  };

  const isolveAlerta = sessao?.alerta ?? null;
  const isolveClicavel = isolveAlerta === 'EXPIRADA' || isolveAlerta === 'SEM_SESSAO';

  // ── Sidebar (compartilhada entre desktop e off-canvas mobile) ─────────────
  const Sidebar = (
    <aside
      className={`flex flex-col bg-white border-r border-otm-border h-full shrink-0 transition-[width] duration-200 ${
        colapsada ? 'w-[68px]' : 'w-[248px]'
      }`}
    >
      {/* Marca + colapso */}
      <div className="h-14 flex items-center gap-2 px-3 border-b border-otm-border shrink-0">
        {colapsada ? (
          <div className="w-9 h-9 rounded-lg bg-otm-navy text-white flex items-center justify-center font-bold text-sm mx-auto">
            OC
          </div>
        ) : (
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); irPara('/'); }}
            className="flex items-center min-w-0"
            title="OtmizCred | CRM"
          >
            <img src="/assets/logo-principal-251x31.webp" alt="OtmizCred" className="h-7 w-auto" />
            <span className="ml-2 text-xs font-semibold text-otm-txt-3">CRM</span>
          </a>
        )}
        {/* Toggle colapso - só desktop */}
        <button
          onClick={alternarColapso}
          title={colapsada ? 'Expandir menu' : 'Recolher menu'}
          className="hidden md:flex ml-auto w-6 h-6 items-center justify-center rounded-md text-otm-txt-3 hover:bg-otm-subtle hover:text-otm-txt-2 transition-colors"
        >
          {colapsada ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
        {/* Fechar off-canvas - só mobile */}
        <button
          onClick={() => setMobileAberto(false)}
          className="md:hidden ml-auto w-8 h-8 flex items-center justify-center rounded-md text-otm-txt-3 hover:bg-otm-subtle"
        >
          <X size={18} />
        </button>
      </div>

      {/* Navegação */}
      <nav className="flex-1 overflow-y-auto py-3 space-y-2">
        {SECOES_NAV.map((secao) => (
          <div key={secao.titulo}>
            {!colapsada && (
              <p className="px-4 pt-2 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-otm-txt-3">
                {secao.titulo}
              </p>
            )}
            <ul className="space-y-0.5 px-2">
              {secao.itens.map((item) => {
                const Icone = item.icon;
                const badge = item.badge === 'divergencias' ? divergenciasPendentes : null;
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      onClick={() => setMobileAberto(false)}
                      title={colapsada ? item.label : undefined}
                      className={({ isActive }) =>
                        `relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                          colapsada ? 'justify-center' : ''
                        } ${
                          isActive
                            ? 'bg-otm-navy/10 text-otm-navy font-semibold'
                            : 'text-otm-txt-2 hover:bg-otm-subtle hover:text-otm-txt-1'
                        }`
                      }
                    >
                      <Icone size={18} className="shrink-0" />
                      {!colapsada && <span className="truncate">{item.label}</span>}
                      {!!badge && badge > 0 && (
                        <span
                          className={`min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center ${
                            colapsada ? 'absolute top-1 right-1' : 'ml-auto'
                          }`}
                        >
                          {badge > 9 ? '9+' : badge}
                        </span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Rodapé - usuário */}
      <div className="border-t border-otm-border p-3 shrink-0">
        <div className={`flex items-center gap-2 ${colapsada ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-otm-navy/10 text-otm-navy flex items-center justify-center font-semibold text-xs shrink-0">
            {iniciais}
          </div>
          {!colapsada && (
            <div className="min-w-0">
              <p className="text-xs font-medium text-otm-txt-1 truncate">{nomeExibicao}</p>
              {papelLabel && <p className="text-[10px] text-otm-txt-3 truncate">{papelLabel}</p>}
            </div>
          )}
        </div>
      </div>
    </aside>
  );

  return (
    <div className="h-screen flex bg-otm-surface overflow-hidden">
      {/* Sidebar desktop */}
      <div className="hidden md:flex h-full">{Sidebar}</div>

      {/* Off-canvas mobile */}
      {mobileAberto && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileAberto(false)} />
          <div className="md:hidden fixed inset-y-0 left-0 z-50 animate-in slide-in-from-left duration-200">
            {Sidebar}
          </div>
        </>
      )}

      {/* Coluna de conteúdo */}
      <div className="flex-1 flex flex-col min-w-0 h-full">
        {/* Banner de ambiente — HML sem conexão com o iSolve (OTMC, 31/10/2026).
            Dirigido pelo backend (GET /api/ambiente); o PROD fica conectado e
            nunca mostra esta barra, mesmo com o `main` compartilhado. */}
        {isolveDesconectado && (
          <div
            role="status"
            className="bg-red-600 text-white text-xs sm:text-sm font-medium px-4 py-2 flex items-center justify-center gap-2 text-center shrink-0"
          >
            <AlertTriangle size={16} className="shrink-0" />
            <span>
              Ambiente de {ambiente?.ambiente ?? 'Homologação'} — sem conexão com o iSolve.
              Os dados são uma cópia e não são refletidos no iSolve de produção.
            </span>
          </div>
        )}

        {/* Topbar */}
        <header className="h-14 bg-white border-b border-otm-border flex items-center justify-between gap-3 px-3 sm:px-4 shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileAberto(true)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-otm-txt-2 hover:bg-otm-subtle"
              title="Abrir menu"
            >
              <Menu size={20} />
            </button>
          </div>

          <div className="flex items-center gap-1.5">
            {/* Semáforo iSolve — oculto quando a conexão está desligada (banner
                vermelho já comunica o estado; evita sinal contraditório) */}
            {isolveAlerta && !isolveDesconectado && (
              <button
                onClick={isolveClicavel ? () => irPara('/conectar-isolve') : undefined}
                title={ISOLVE_CFG[isolveAlerta].titulo(sessao?.expiraEmDias ?? null)}
                className={`hidden sm:inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg text-otm-txt-2 hover:bg-otm-subtle transition-colors ${
                  isolveClicavel ? 'cursor-pointer' : 'cursor-default'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${ISOLVE_CFG[isolveAlerta].cor} ring-2 ${ISOLVE_CFG[isolveAlerta].anel}`} />
                iSolve
              </button>
            )}

            {/* Engrenagem (Integrações e Sistema) — todos os usuários (operador acessa
                Extensões e iSolve); itens administrativos ficam gated dentro do modal. */}
            <button
              onClick={() => setConfigAberto(true)}
              className="p-2 rounded-lg hover:bg-otm-subtle text-otm-txt-2 transition-colors"
              title="Integrações e Sistema"
            >
              <Settings size={20} />
            </button>

            {/* Notificações (Central de Comunicação - Fase B) */}
            <SinoNotificacoes />

            {/* Avatar + menu */}
            <div className="relative">
              <button
                onClick={() => setMenuUsuario((v) => !v)}
                className="flex items-center gap-2 p-1 rounded-lg hover:bg-otm-subtle transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-otm-navy/10 text-otm-navy flex items-center justify-center font-semibold text-sm">
                  {iniciais}
                </div>
                <span className="hidden md:inline text-sm text-otm-txt-2 max-w-[120px] truncate">
                  {nomeExibicao.split(' ')[0]}
                </span>
              </button>

              {menuUsuario && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuUsuario(false)} />
                  <div className="absolute right-0 mt-1 w-56 bg-white rounded-xl shadow-lg border border-otm-border py-1 z-50">
                    <div className="px-4 py-2 border-b border-otm-border">
                      <p className="text-sm font-medium text-otm-txt-1 truncate">{nomeExibicao}</p>
                      <p className="text-xs text-otm-txt-3 truncate">{conta?.username}</p>
                      {papelLabel && <p className="text-xs text-otm-navy font-medium mt-0.5">{papelLabel}</p>}
                    </div>
                    <button
                      onClick={() => irPara('/conectar-isolve')}
                      className="w-full text-left px-4 py-2 text-sm text-otm-txt-2 hover:bg-otm-subtle transition-colors flex items-center gap-2"
                    >
                      <Link2 size={16} className="text-otm-txt-3" />
                      Conectar iSolve
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                    >
                      <LogOut size={16} />
                      Sair
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Conteúdo das páginas */}
        <main className="flex-1 min-h-0 overflow-auto">
          <Outlet context={{ currentUser } satisfies ShellContext} />
        </main>
      </div>

      {/* Modal de Integrações e Sistema (centralizado) - todos; itens admin gated */}
      {configAberto && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70] p-4"
          onClick={() => setConfigAberto(false)}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-otm-border">
              <h3 className="text-base font-bold text-otm-txt-1 flex items-center gap-2">
                <Settings size={18} className="text-otm-orange" />
                Integrações e Sistema
              </h3>
              <button onClick={() => setConfigAberto(false)} className="text-otm-txt-3 hover:text-otm-txt-1">
                <X size={20} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-5">
              {/* Integrações */}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-otm-txt-3 mb-2">Integrações</p>
                <div className="space-y-1">
                  <button
                    onClick={() => irPara('/conectar-isolve')}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-otm-border hover:bg-otm-subtle transition-colors text-left"
                  >
                    <span className="flex items-center gap-2 text-sm text-otm-txt-1">
                      <Link2 size={16} className="text-otm-txt-3" />
                      iSolve
                    </span>
                    {isolveAlerta && (
                      <span className={`w-2 h-2 rounded-full ${ISOLVE_CFG[isolveAlerta].cor}`} />
                    )}
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => irPara('/integracoes/ploomes')}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-otm-border hover:bg-otm-subtle transition-colors text-left"
                    >
                      <span className="flex items-center gap-2 text-sm text-otm-txt-1">
                        <Download size={16} className="text-otm-txt-3" />
                        Ploomes
                      </span>
                      <span className="text-[10px] font-medium text-otm-orange bg-otm-orange/10 rounded-full px-2 py-0.5">importar</span>
                    </button>
                  )}
                  <IntegracaoEmBreve icon={FolderOpen} nome="SharePoint" />
                  <IntegracaoEmBreve icon={Mail} nome="E-mail" />
                  <IntegracaoEmBreve icon={Grid3x3} nome="Microsoft 365" />
                </div>
              </div>
              {/* Sistema */}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-otm-txt-3 mb-2">Sistema</p>
                <div className="space-y-1">
                  {/* Extensões — todos os usuários (operador gera token e baixa a extensão) */}
                  <button
                    onClick={() => irPara('/extensoes')}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-otm-border hover:bg-otm-subtle transition-colors text-left"
                  >
                    <span className="flex items-center gap-2 text-sm text-otm-txt-1">
                      <Puzzle size={16} className="text-otm-txt-3" />
                      Extensões
                    </span>
                    <span className="text-[10px] font-medium text-otm-orange bg-otm-orange/10 rounded-full px-2 py-0.5">iSolve</span>
                  </button>
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => irPara('/admin/extensoes-tokens')}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-otm-border hover:bg-otm-subtle transition-colors text-left text-sm text-otm-txt-1"
                      >
                        <KeyRound size={16} className="text-otm-txt-3" />
                        Tokens
                      </button>
                      <button
                        onClick={() => irPara('/admin/usuarios')}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-otm-border hover:bg-otm-subtle transition-colors text-left text-sm text-otm-txt-1"
                      >
                        <Users size={16} className="text-otm-txt-3" />
                        Usuários <span className="text-[10px] text-otm-txt-3 font-medium">(RBAC)</span>
                      </button>
                      <button
                        onClick={() => irPara('/admin/parametros-instituicao')}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-otm-border hover:bg-otm-subtle transition-colors text-left text-sm text-otm-txt-1"
                      >
                        <Building2 size={16} className="text-otm-txt-3" />
                        Instituições
                      </button>
                      <button
                        onClick={() => irPara('/admin/etapas-esteira')}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-otm-border hover:bg-otm-subtle transition-colors text-left text-sm text-otm-txt-1"
                      >
                        <ListOrdered size={16} className="text-otm-txt-3" />
                        Etapas das Esteiras
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Linha de integração ainda não disponível (sem tela de config própria ainda).
function IntegracaoEmBreve({ icon: Icone, nome }: { icon: typeof Kanban; nome: string }) {
  return (
    <div
      className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-otm-border bg-otm-subtle/40 cursor-default"
      title="Em breve"
    >
      <span className="flex items-center gap-2 text-sm text-otm-txt-3">
        <Icone size={16} />
        {nome}
      </span>
      <span className="text-[10px] font-medium text-otm-txt-3 bg-white border border-otm-border rounded-full px-2 py-0.5">
        em breve
      </span>
    </div>
  );
}
